import { Button } from '@/app/common/components/button/Button'
import { inputVariants } from '@/app/common/components/input/inputVariants'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { currencyFormatter } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import CurrencyInput from 'react-currency-input-field'
import { z } from 'zod'
import type { ProgramPurseResource } from '@/queries/program_purses/schemas.ts'
import { useRemovePurseFunds } from '@/queries/program_purses/useRemovePurseFunds.ts'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shad/ui/form'
import { useForm } from 'react-hook-form'

interface RemoveFundsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  purse: ProgramPurseResource
  programId: number
}

type RemoveFundsFormValues = {
  amount: string
}

export function RemoveFundsDialog({
  open,
  onOpenChange,
  purse,
  programId,
}: RemoveFundsDialogProps) {
  const { toast } = useToast()

  const removeFundsSchema = z.object({
    amount: z
      .string()
      .min(1, 'Amount is required')
      .refine((val) => !isNaN(Number(val)) && Number(val) > 0, {
        message: 'Amount must be a positive number',
      })
      .refine((val) => Number(val) <= purse.balance_available, {
        message: `Amount cannot exceed ${currencyFormatter.format(
          purse.balance_available
        )}`,
      }),
  })

  const form = useForm<RemoveFundsFormValues>({
    resolver: zodResolver(removeFundsSchema),
    defaultValues: {
      amount: '',
    },
  })

  const removeFundsMutation = useRemovePurseFunds({
    onSuccess: (_, variables) => {
      toast({
        variant: 'default',
        title: 'Funds Removed',
        description: `Successfully removed ${currencyFormatter.format(
          variables.amount
        )} from purse ${purse.name}.`,
      })

      form.reset()
      onOpenChange(false)
    },
    onError: (error: RequestError) => {
      toast({
        variant: 'destructive',
        title: 'Remove Funds Failed',
        description: error.message,
      })
    },
  })

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  const parseCurrencyValue = (value?: string) =>
    Number(value?.replace(/[^\d.-]+/g, '') || 0)

  const onSubmit = (values: RemoveFundsFormValues) => {
    removeFundsMutation.mutate({
      programId,
      purseId: purse.id,
      amount: parseCurrencyValue(values.amount),
    })
  }

  const removeAmount = parseCurrencyValue(form.watch('amount'))

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
          return
        }

        onOpenChange(nextOpen)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove Funds</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="min-w-0 space-y-4"
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      id="remove-funds-amount"
                      name={field.name}
                      decimalsLimit={2}
                      min={1}
                      prefix="$"
                      value={field.value}
                      onBlur={field.onBlur}
                      onValueChange={(value) => {
                        field.onChange(value || '')
                      }}
                      placeholder="Enter amount"
                      required
                      className={inputVariants({
                        variant: 'text',
                      })}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h4 className="font-medium text-gray-900">Removal Summary</h4>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Available Balance:</span>
                <span className="font-medium">
                  {currencyFormatter.format(purse.balance_available)}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount to Remove:</span>
                <span className="font-medium">
                  {currencyFormatter.format(removeAmount)}
                </span>
              </div>

              <div className="border-t pt-2 flex justify-between">
                <span className="font-medium text-gray-900">
                  Remaining Balance:
                </span>
                <span className="font-bold text-green-600">
                  {currencyFormatter.format(
                    Math.max(purse.balance_available - removeAmount, 0)
                  )}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <SubmitButton
                buttonTxt="Remove Funds"
                status={removeFundsMutation.status}
              />
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
